import { createContext, useContext, useMemo, type ReactNode } from "react";

/**
 * "A help tour is running — show what this looks like with data in it."
 *
 * An empty screen cannot be explained. The wallet with no transactions, the
 * orders list with no orders, the reviews page before a first review: the help
 * tour points at a region and the seller sees the same blank they were already
 * looking at. While a tour runs, those regions render a few SAMPLE rows instead
 * — clearly labelled as an example — so the explanation has something to
 * explain.
 *
 * A context rather than a prop so any component at any depth can opt in without
 * its parents having to thread a flag through, and so the editor can reuse it.
 * Sample data must NEVER be interactive or savable: it is a picture, and it
 * disappears with the tour.
 */
type HelpDemoState = {
  /** A tour is running on this page. */
  active: boolean;
  /**
   * The anchor of the step the seller is on, or null. A page uses this to put
   * itself into the state the step describes — the customers screen opens a
   * profile when the tour reaches `customer-detail`, because on a phone that
   * profile is a full screen that does not exist until something opens it.
   * Doing it up front instead covered the list the previous step explains.
   */
  anchor: string | null;
};

const HelpDemoContext = createContext<HelpDemoState>({
  active: false,
  anchor: null,
});

export function HelpDemoProvider({
  active,
  anchor,
  children,
}: {
  active: boolean;
  anchor: string | null;
  children: ReactNode;
}) {
  const value = useMemo(() => ({ active, anchor }), [active, anchor]);
  return (
    <HelpDemoContext.Provider value={value}>{children}</HelpDemoContext.Provider>
  );
}

/** True while a page-help tour is running. */
export function useHelpDemo(): boolean {
  return useContext(HelpDemoContext).active;
}

/** The anchor of the step the tour is on, or null. */
export function useHelpStep(): string | null {
  return useContext(HelpDemoContext).anchor;
}
